import { Trans } from '@lingui/macro';
import type { BackendTypes } from '@tf/api';
import type { FormControl } from '@tf/form-builder';
import { FormBuilder } from '@tf/form-builder';
import { TFPageContent } from '@tf/shared';
import { Box, Button, createStyles, Flex, Stack, Title, useMantineTheme } from '@tf/ui';
import type { SubmitHandler } from '@tf/utils';
import type React from 'react';
import { useMemo } from 'react';
import { MarkdownText } from '@/components/shared';
import { useAppEnv, useDefinitions } from '@/core/providers';

const useStyles = createStyles({
	markdown: {
		color: '#6B7280',
		textAlign: 'center',

		'& h1, h2, h3, h4, h5': {
			fontWeight: 600,
		},

		'& p, ul, ol': {
			marginTop: '12px',
			marginBottom: '0',
		},
	},
});

type Props = {
	label: string;
	description: string;
	segmentContainers: BackendTypes.SegmentContainer[];
	readOnly?: boolean;
	renderPreSubmitSection?: () => React.ReactNode;
	renderSubmit?: (isLoading: boolean) => React.ReactNode;
	onSubmit: SubmitHandler;
	submitTypeRef?: React.MutableRefObject<'save' | 'continue' | 'quietSave'>;
	formControlRef?: React.MutableRefObject<FormControl | null>;
};

export const Form: React.FC<Props> = ({
	label,
	description,
	segmentContainers,
	readOnly = false,
	renderPreSubmitSection,
	renderSubmit,
	submitTypeRef,
	formControlRef,
	onSubmit,
}) => {
	const { colors } = useMantineTheme();
	const { classes } = useStyles();

	const cardStyles = useMemo(() => {
		return {
			marginBottom: '1rem',
			padding: '1.5rem',
			borderStyle: 'solid',
			borderRadius: 8,
			borderWidth: 0.5,
			borderColor: colors.gray[2],
			backgroundColor: '#fff',
			boxShadow: '0 4px 20px -4px rgb(0 0 0 / 10%)',
		};
	}, [colors.gray]);

	const appEnv = useAppEnv();
	const definitions = useDefinitions();

	const initialValues = segmentContainers.reduce(
		(acc, sc) => {
			return {
				...acc,
				[sc.info.SegmentIdentity.segmentKind]: sc.segment,
			};
		},
		{} as Record<BackendTypes.SegmentKind, BackendTypes.Segment>,
	);

	const segments = segmentContainers.map((sc) => {
		return {
			segmentKind: sc.info.SegmentIdentity.segmentKind,
			graphId: sc.info.SegmentIdentity.graphId,
			accessMode: readOnly ? 'VIEW' : sc.info.AccessMode,
		};
	});

	const isLongDescription = description.length > 296;
	return (
		<TFPageContent maw="50rem" m="0">
			<Stack mb={isLongDescription ? '16px' : '24px'} gap="0" align="center">
				<Title mt="16px" fz="32px" lh="40px" fw="600" order={2}>
					{label}
				</Title>
				{isLongDescription ? (
					<Box m={`${isLongDescription ? '24px' : '16px'} 0 0 0`} sx={cardStyles}>
						<MarkdownText text={description} />
					</Box>
				) : (
					<MarkdownText className={classes.markdown} text={description} />
				)}
			</Stack>

			<FormBuilder
				disableAutocomplete
				styles={{ segment: cardStyles }}
				initialValues={initialValues}
				validateInitialValues={segmentContainers.some((s) => s.validationStatus === 'VALIDATION_ERRORS')}
				segments={segments}
				currency={definitions.currency}
				defs={{
					structs: definitions.defs.structs,
					options: definitions.defs.options,
				}}
				processingScripts={definitions.scripts}
				apiConfig={{
					username: '',
					clusterId: appEnv.clusterId,
				}}
				formControlRef={formControlRef}
				submitButtonRenderer={(isLoading) => {
					return (
						<>
							{renderPreSubmitSection ? <Box sx={cardStyles}>{renderPreSubmitSection()}</Box> : null}

							<Box
								sx={{
									...cardStyles,
									display: 'flex',
									justifyContent: 'end',
									position: 'sticky',
									bottom: '10px',
									zIndex: 101,
									padding: '12px',
								}}
							>
								{renderSubmit ? (
									renderSubmit(isLoading)
								) : (
									<Flex>
										<Button
											onClick={(e) => {
												if (!submitTypeRef) return;
												submitTypeRef.current = 'save';
												e.currentTarget.form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
											}}
											name="save"
											variant="light"
											size="md"
											loading={isLoading}
										>
											<Trans id="applicationForm.saveButton">Save changes</Trans>
										</Button>
										<Button
											onClick={(e) => {
												if (!submitTypeRef) return;
												submitTypeRef.current = 'continue';
												e.currentTarget.form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
											}}
											name="save-and-continue"
											ml="12px"
											size="md"
											loading={isLoading}
										>
											<Trans id="applicationForm.saveAndContinueButton">Save and continue</Trans>
										</Button>
									</Flex>
								)}
							</Box>
						</>
					);
				}}
				onSubmit={onSubmit}
			/>
		</TFPageContent>
	);
};
